// Deterministic, rules-based optimization scoring — no LLM call, ever.
// Runs entirely client-side so it can update on every keystroke. Scores
// are heuristic completeness/readiness indicators, NOT a prediction of
// rankings, reach, or engagement — every surface that shows a score must
// say so (see OptimizeReviewPanel.jsx's disclaimer copy).
//
// Platform-specific numeric ranges below are editorial defaults (common,
// widely-published platform guidance), not pulled from any live API —
// there is no connected source for this in the codebase (see section 10
// of the spec / search-volume note in optimizeVariants.js).
export const PLATFORM_LIMITS = {
  youtube: { titleMin: 20, titleMax: 70, descMin: 80, hashtagMin: 2, hashtagMax: 5, keywordMin: 3, keywordMax: 15 },
  facebook: { titleMin: 10, titleMax: 60, descMin: 40, hashtagMin: 1, hashtagMax: 3, keywordMin: 2, keywordMax: 10 },
  instagram: { titleMin: 5, titleMax: 60, descMin: 40, hashtagMin: 5, hashtagMax: 15, keywordMin: 2, keywordMax: 15 },
  tiktok: { titleMin: 5, titleMax: 60, descMin: 20, hashtagMin: 3, hashtagMax: 6, keywordMin: 2, keywordMax: 10 },
};
const DEFAULT_LIMITS = PLATFORM_LIMITS.youtube;

const CTA_PHRASES = ['subscribe', 'follow', 'comment', 'share', 'like', 'link in bio', 'watch', 'join', 'sign up', 'learn more', 'save this', 'tag a friend'];

function limitsFor(platform) { return PLATFORM_LIMITS[platform] || DEFAULT_LIMITS; }

function countHashtags(hashtagsStr) {
  return (hashtagsStr || '').split(/[\s,]+/).map((t) => t.trim()).filter((t) => t.startsWith('#') && t.length > 1).length;
}

function dedupeAndCount(keywordBuckets) {
  const all = [...(keywordBuckets.brand || []), ...(keywordBuckets.platform || []), ...(keywordBuckets.content || [])]
    .map((k) => (k || '').trim().toLowerCase()).filter(Boolean);
  const seen = new Set();
  let duplicates = 0;
  all.forEach((k) => { if (seen.has(k)) duplicates += 1; seen.add(k); });
  return { uniqueCount: seen.size, totalCount: all.length, duplicates };
}

function scoreBand(value, min, max, hardMinForAny = 1) {
  // 0 if truly empty, partial credit approaching `min`, full credit inside [min,max], gentle taper past `max`.
  if (!value || value < hardMinForAny) return 0;
  if (value < min) return Math.round((value / min) * 70); // ramps up to 70% right at min
  if (value <= max) return 100;
  const over = value - max;
  return Math.max(60, 100 - over * 5); // mild taper for "too many", never collapses to 0
}

/**
 * @param {Object} input
 * @param {string} input.platform
 * @param {string} input.title
 * @param {string} input.description
 * @param {string} input.cta
 * @param {string} input.hashtags
 * @param {{brand:string[], platform:string[], content:string[]}} input.keywords
 * @param {boolean} input.hasThumbnail
 */
export function scoreVariant({ platform, title = '', description = '', cta = '', hashtags = '', keywords = { brand: [], platform: [], content: [] }, hasThumbnail = false }) {
  const limits = limitsFor(platform);
  const suggestions = [];

  // Completeness (20): one field each for title/description/hashtags/any-keyword.
  const hasAnyKeyword = (keywords.brand?.length || 0) + (keywords.platform?.length || 0) + (keywords.content?.length || 0) > 0;
  const completenessParts = [!!title.trim(), !!description.trim(), countHashtags(hashtags) > 0, hasAnyKeyword];
  const completeness = Math.round((completenessParts.filter(Boolean).length / completenessParts.length) * 20);
  if (!title.trim()) suggestions.push('Add a title — every platform variant needs one before it can be approved.');
  if (!description.trim()) suggestions.push('Add a description or caption.');
  if (countHashtags(hashtags) === 0) suggestions.push('Add at least one hashtag.');
  if (!hasAnyKeyword) suggestions.push('Add at least one keyword from any of the three buckets.');

  // Title quality (15): length in platform's healthy range.
  const titleQuality = Math.round(scoreBand(title.trim().length, limits.titleMin, limits.titleMax) * 0.15);
  if (title.trim() && title.trim().length < limits.titleMin) suggestions.push(`Title is short for ${platform} — aim for ${limits.titleMin}+ characters.`);
  if (title.trim() && title.trim().length > limits.titleMax * 1.5) suggestions.push(`Title is quite long for ${platform} — consider trimming it.`);

  // Description quality (15): length + CTA presence (explicit cta field OR a recognized phrase in the description).
  const descLen = description.trim().length;
  const descLenScore = scoreBand(descLen, limits.descMin, limits.descMin * 4);
  const lowerDesc = description.toLowerCase();
  const hasCtaSignal = !!cta.trim() || CTA_PHRASES.some((p) => lowerDesc.includes(p));
  const descriptionQuality = Math.round(((descLenScore * 0.7) + (hasCtaSignal ? 30 : 0)) * 0.15);
  if (description.trim() && !hasCtaSignal) suggestions.push('No call-to-action detected — add one (e.g. "Follow for more", "Link in bio").');
  if (description.trim() && descLen < limits.descMin) suggestions.push(`Description is brief for ${platform} — a bit more detail usually helps.`);

  // Keyword usage (15): unique count vs platform range, minus a duplicate penalty.
  const { uniqueCount, duplicates } = dedupeAndCount(keywords);
  const keywordBandScore = scoreBand(uniqueCount, limits.keywordMin, limits.keywordMax);
  const duplicatePenalty = Math.min(40, duplicates * 10);
  const keywordUsage = Math.round(Math.max(0, keywordBandScore - duplicatePenalty) * 0.15);
  if (uniqueCount < limits.keywordMin) suggestions.push(`Add more keywords — ${platform} variants do best with ${limits.keywordMin}+ unique keywords across the three buckets.`);
  if (duplicates > 0) suggestions.push(`Remove ${duplicates} duplicate keyword${duplicates > 1 ? 's' : ''} repeated across buckets.`);

  // Hashtag usage (15): count vs platform range.
  const hashtagCount = countHashtags(hashtags);
  const hashtagUsage = Math.round(scoreBand(hashtagCount, limits.hashtagMin, limits.hashtagMax) * 0.15);
  if (hashtagCount > 0 && hashtagCount < limits.hashtagMin) suggestions.push(`${platform} posts typically use at least ${limits.hashtagMin} hashtags.`);
  if (hashtagCount > limits.hashtagMax * 2) suggestions.push(`That's a lot of hashtags for ${platform} — consider trimming to your strongest ${limits.hashtagMax}.`);

  // Platform-specific required-field readiness (10): a tiny per-platform rule, not a guarantee of platform policy compliance.
  let platformFields = 10;
  if (platform === 'youtube' && !description.trim()) { platformFields -= 5; suggestions.push('YouTube strongly favors videos with a filled-in description.'); }
  if (platform === 'tiktok' && hashtagCount === 0) { platformFields -= 5; suggestions.push('TikTok captions typically need hashtags to be discoverable.'); }
  if (platform === 'instagram' && hashtagCount === 0) { platformFields -= 5; suggestions.push('Instagram posts typically need hashtags to be discoverable.'); }
  if (platform === 'facebook' && !description.trim()) { platformFields -= 5; suggestions.push('Facebook posts need a description/caption.'); }
  platformFields = Math.max(0, platformFields);

  // Thumbnail readiness (10): binary — has one resolved or not.
  const thumbnailReadiness = hasThumbnail ? 10 : 0;
  if (!hasThumbnail) suggestions.push('No thumbnail linked yet — pick or capture one in the Thumbnails panel.');

  const components = { completeness, titleQuality, descriptionQuality, keywordUsage, hashtagUsage, platformFields, thumbnailReadiness };
  const overall = Math.max(0, Math.min(100, Object.values(components).reduce((a, b) => a + b, 0)));

  return { overall, components, suggestions, computedAt: new Date().toISOString() };
}

export function scoreLabel(overall) {
  if (overall >= 85) return { label: 'Strong', color: '#39E6C8' };
  if (overall >= 65) return { label: 'Good', color: '#3A7BFF' };
  if (overall >= 40) return { label: 'Needs work', color: '#F5A623' };
  return { label: 'Incomplete', color: '#F5547C' };
}
